import { useState } from 'react';
import { useAccountantExports, useCreateAccountantExport, useMarkAccountantYearFiled, useReopenAccountantYear } from '@/api/hooks/useAccountantExports';
import { ExportCover } from './ExportCover';
import { ExportFormatList } from './ExportFormatList';
import { MarkFiledDialog } from './MarkFiledDialog';
import { ReopenDialog } from './ReopenDialog';
import { VersionHistory } from './VersionHistory';
import { YearLockBanner } from './YearLockBanner';

export function AccountantExportPage({ grantId, taxYear }: { grantId: string; taxYear: number }) {
  const query = useAccountantExports(grantId, taxYear);
  const create = useCreateAccountantExport();
  const markFiled = useMarkAccountantYearFiled();
  const reopen = useReopenAccountantYear();
  const [dialog, setDialog] = useState<'filed' | 'reopen' | null>(null);
  const latest = query.data?.versions[0];
  const locked = query.data?.year_lock.locked ?? false;

  function requestPackage() {
    create.mutate({ grantId, taxYear, formats: ['csv', 'pdf', 'xlsx', 'qbo', 'iif'], expectedStateVersion: query.data?.year_lock.state_version ?? 1, idempotencyKey: crypto.randomUUID() });
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6" aria-labelledby="accountant-export-heading">
      <div>
        <h2 id="accountant-export-heading" tabIndex={-1} className="text-title font-semibold">Export package</h2>
        <p className="text-sm text-muted-foreground">Schedule C records · {taxYear} · Business only</p>
      </div>
      {query.isLoading && <p role="status" aria-label="Loading export package">Loading export package…</p>}
      {query.isError && <p role="alert">Export package is unavailable. Try again.</p>}
      {!query.isLoading && !query.isError && latest && (
        <>
          {locked && <YearLockBanner version={query.data?.year_lock.version ?? latest.version} filedAt={query.data?.year_lock.filed_at ?? null} />}
          <ExportCover version={latest} />
          {!locked && <button type="button" className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground" onClick={requestPackage}>Download package · .zip</button>}
          <ExportFormatList version={latest} grantId={grantId} taxYear={taxYear} locked={locked} />
          {!locked && <button type="button" className="min-h-11 rounded-md border border-border px-4 py-2 text-sm" onClick={() => setDialog('filed')}>Mark version {latest.version} as filed</button>}
          {locked && <button type="button" className="min-h-11 rounded-md border border-border px-4 py-2 text-sm" onClick={() => setDialog('reopen')}>Reopen with reason…</button>}
          <VersionHistory versions={query.data?.versions ?? []} />
          {dialog === 'filed' && <MarkFiledDialog unresolved={latest.unresolved.open_items} onCancel={() => setDialog(null)} onConfirm={() => { setDialog(null); markFiled.mutate({ grantId, taxYear, exportVersionId: latest.export_version_id, expectedStateVersion: query.data?.year_lock.state_version ?? 1, acknowledgeUnresolved: true }); }} />}
          {dialog === 'reopen' && <ReopenDialog onCancel={() => setDialog(null)} onConfirm={(reason) => { setDialog(null); reopen.mutate({ grantId, taxYear, expectedStateVersion: query.data?.year_lock.state_version ?? 1, reason }); }} />}
        </>
      )}
      {!query.isLoading && !query.isError && !latest && <><p role="status">No export versions yet.</p><button type="button" className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground" onClick={requestPackage}>Create export package</button></>}
    </div>
  );
}
